'use client'

// One-file window.fetch wrapper: stamps `x-ui-session` (the lib/ui-log
// browsing-session id) onto same-origin API mutations and server-action
// posts, so the action_log rows they produce carry the same session_id as
// the UI telemetry rows around them — one query replays the whole session.
//
// Why a patch and not a shared apiFetch helper: 282 raw `fetch(` call sites
// across 104 client files would need a codemod that silently decays (nothing
// stops the 283rd raw fetch), and React owns the fetch behind server actions
// outright. One chokepoint covers all of them, present and future, and is
// removable by deleting one import. Sentry already wraps fetch in this same
// init path — the pattern is established. Design rules that keep it safe:
//   - header-add ONLY; never touches body, method, signal, or the response
//     (streaming and aborts pass through untouched)
//   - same-origin only, and only /api/ requests or next-action posts — RSC
//     payload fetches and prefetch GETs are never modified
//   - any internal error falls through to the ORIGINAL call, unmodified
//   - idempotent install (module flag + window marker survives HMR)
//
// It also announces same-origin /api/ activity via a DOM event so the
// interaction tracker's dead-click detector can tell "click did something"
// from "click did nothing".

import { getUiLogSessionId } from '@/lib/ui-log'

/** DOM event dispatched for same-origin /api/ requests (dead-click signal). */
export const FETCH_EFFECT_EVENT = 'sb:fetch-effect'

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const MARKER = '__sbSessionFetchPatched'

function headersHas(init: RequestInit | undefined, input: RequestInfo | URL, name: string): boolean {
  try {
    if (init?.headers) {
      const h = new Headers(init.headers)
      if (h.has(name)) return true
    }
    if (input instanceof Request && input.headers.has(name)) return true
  } catch {
    /* malformed headers — treat as absent */
  }
  return false
}

export function installSessionFetchPatch(): void {
  try {
    if (typeof window === 'undefined') return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    if (w[MARKER]) return
    w[MARKER] = true

    const originalFetch = window.fetch.bind(window)

    window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      try {
        const url = new URL(
          input instanceof Request ? input.url : String(input),
          window.location.origin,
        )
        if (url.origin !== window.location.origin) return originalFetch(input, init)

        const method = (
          init?.method ??
          (input instanceof Request ? input.method : 'GET')
        ).toUpperCase()

        const isApi = url.pathname.startsWith('/api/')
        const isServerAction =
          method === 'POST' && headersHas(init, input, 'next-action')

        if (isApi) {
          try {
            window.dispatchEvent(new Event(FETCH_EFFECT_EVENT))
          } catch {
            /* dead-click signal is best-effort */
          }
        }

        const wantsHeader =
          (isApi && MUTATION_METHODS.has(method)) || isServerAction
        if (!wantsHeader) return originalFetch(input, init)

        const sessionId = getUiLogSessionId()
        if (!sessionId) return originalFetch(input, init)

        if (init?.headers || !(input instanceof Request)) {
          const headers = new Headers(init?.headers)
          headers.set('x-ui-session', sessionId)
          return originalFetch(input, { ...init, headers })
        }
        // Request input with no init headers: its Headers guard is 'request',
        // which permits setting non-forbidden names in place.
        input.headers.set('x-ui-session', sessionId)
        return originalFetch(input, init)
      } catch {
        // Never let instrumentation break a request.
        return originalFetch(input, init)
      }
    }
  } catch (err) {
    console.debug('[session-fetch-patch] install failed:', err)
  }
}
